import './App.css';
import React, { useState, useEffect } from 'react';
import { autoDetectFirebase, subscribeToCollection, saveDocument, deleteDocument } from './firebase';

// Import Custom Modular Components
import FlightTrackerV3 from './components/FlightTrackerV3';
import RoomAssignments from './components/RoomAssignments';
import CarAssignments from './components/CarAssignments';
import GroceryList from './components/GroceryList';
import DinnerDishes from './components/DinnerDishes';
import LocalActivities from './components/LocalActivities';
import BookClub from './components/BookClub';
import Schedule from './components/Schedule';
import PackingList from './components/PackingList';
import DiscussionThread from './components/DiscussionThread';
import VoteButton from './components/VoteButton';
import { TRIP_START_DATE } from './utils/dateUtils';

// Tab mapping for URL hash routing
const TAB_MAP = {
  dashboard: "Dashboard",
  flights: "Flights",
  rooms: "Rooms",
  cars: "Cars",
  grocery: "Grocery",
  dinners: "Dinners",
  activities: "Activities",
  bookclub: "BookClub",
  schedule: "Schedule",
  packing: "Packing"
};

function App() {
  const [password, setPassword] = useState("");
  const [userNameInput, setUserNameInput] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [authError, setAuthError] = useState("");

  // Real-time Users from Firestore
  const [users, setUsers] = useState([]);

  // Live subscription & auto-seeding of users
  useEffect(() => {
    const unsubscribe = subscribeToCollection("users", (data) => {
      if (data.length === 0) {
        // Prepopulate users collection with default family members
        const INITIAL_FAM_NAMES = [
          "Aisa", "Alison", "Autumn", "Barb", "Benny", "Brooks", "Caroline", "Daniel", "Elizabeth",
          "Harper", "Henry", "Jacob", "Joel", "Katie", "Kelley", "Linda", "Mabel", "Martín",
          "Nate", "Paul", "Reilan", "Roshaun", "Sam", "Shay", "Simon", "Sonoka", "Valerie"
        ];
        INITIAL_FAM_NAMES.forEach(async (name, idx) => {
          const u = { id: String(idx + 1), name };
          await saveDocument("users", u.id, u);
        });
      } else {
        const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
        setUsers(sorted);
      }
    });
    return () => unsubscribe();
  }, []);



  // Sync hash to activeTab on load / login / hashchange
  useEffect(() => {
    if (!isAuthorized) return;

    const handleHashChange = () => {
      let hash = window.location.hash.slice(1).toLowerCase();
      if (hash.includes('?')) {
        hash = hash.split('?')[0];
      }
      if (TAB_MAP[hash]) {
        setActiveTab(TAB_MAP[hash]);
      }
    };

    handleHashChange(); // Run once when authorized to pick up initial link

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [isAuthorized]);

  // Sync activeTab to hash
  useEffect(() => {
    if (!isAuthorized) return;
    let currentHash = window.location.hash.slice(1).toLowerCase();
    if (currentHash.includes('?')) {
      currentHash = currentHash.split('?')[0];
    }
    const targetHash = activeTab.toLowerCase();
    if (currentHash !== targetHash) {
      window.location.hash = targetHash;
    }
  }, [activeTab, isAuthorized]);

  // Scroll active tab into view in mobile scrollable navbar
  useEffect(() => {
    if (!isAuthorized) return;
    const timer = setTimeout(() => {
      const activeEl = document.querySelector('.sidebar .nav-item.active');
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [activeTab, isAuthorized]);

  const nameInputRef = React.useRef(null);
  const passwordInputRef = React.useRef(null);

  // Light/Dark Theme management (Default to light mode)
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("prine_vacation_theme") || "light";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("prine_vacation_theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === "light" ? "dark" : "light");
  };

  // Auto-detect Firebase environment details dynamically on load
  useEffect(() => {
    autoDetectFirebase();
  }, []);

  // Persist session & load saved name
  useEffect(() => {
    const savedAuth = localStorage.getItem("prine_vacation_auth");
    if (savedAuth === "true") {
      setIsAuthorized(true);
    }
    const savedName = localStorage.getItem("prine_user_name");
    if (savedName) {
      setUserNameInput(savedName);
    }
  }, []);

  const handleLogout = () => {
    setIsAuthorized(false);
    setPassword("");
    setUserNameInput("");
    localStorage.removeItem("prine_vacation_auth");
    localStorage.removeItem("prine_user_name");
  };

  // Autofocus the correct input field on password gate mount
  useEffect(() => {
    if (!isAuthorized) {
      const timer = setTimeout(() => {
        const savedName = localStorage.getItem("prine_user_name");
        if (savedName && passwordInputRef.current) {
          passwordInputRef.current.focus();
        } else if (nameInputRef.current) {
          nameInputRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAuthorized]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();

    const matchedUser = users.find(u => String(u.id) === String(userNameInput));
    if (!matchedUser) {
      setAuthError("Please select your name.");
      return;
    }

    if (password.toLowerCase() === "edwards") {
      setIsAuthorized(true);
      setAuthError("");
      localStorage.setItem("prine_vacation_auth", "true");
      localStorage.setItem("prine_user_name", matchedUser.id);
      setUserNameInput(matchedUser.id);
    } else {
      setAuthError("Incorrect password.");
    }
  };


  if (!isAuthorized) {
    return (
      <div className="password-gate">
        <div className="password-card" style={{ position: 'relative' }}>
          <button
            type="button"
            className="theme-toggle-btn-gate"
            onClick={toggleTheme}
            title="Toggle Light/Dark Mode"
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              transition: 'all 0.2s',
              zIndex: 10
            }}
          >
            {theme === "light" ? "🌙" : "☀️"}
          </button>
          <span className="password-logo">☀️</span>
          <h1>Prine Family Vacation</h1>
          <p className="password-subtitle">
            Welcome to the family vacation portal.<br />
            Please enter the password to access the website.
          </p>
          <form onSubmit={handlePasswordSubmit}>
            <div className="password-input-group" style={{ marginBottom: '16px' }}>
              <select
                ref={nameInputRef}
                className="name-input"
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid var(--color-glass-border)',
                  color: 'var(--color-text-main)',
                  padding: '12px',
                  borderRadius: '12px',
                  fontFamily: 'inherit',
                  fontSize: '0.95rem',
                  outline: 'none',
                  cursor: 'pointer'
                }}
                value={userNameInput}
                onChange={e => setUserNameInput(e.target.value)}
                required
              >
                <option value="" style={{ background: 'var(--color-bg-dropdown, #1e293b)', color: '#94a3b8' }}>-- Select Your Name --</option>
                {users.map(u => (
                  <option key={u.id} value={u.id} style={{ background: 'var(--color-bg-dropdown, #1e293b)', color: 'var(--color-text-main)' }}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="password-input-group">
              <input
                type="password"
                ref={passwordInputRef}
                className="password-input"
                placeholder="********"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="password-btn">
              Unlock Portal 🔑
            </button>
            {authError && <div className="password-error">{authError}</div>}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <button
        className="theme-toggle-btn"
        onClick={toggleTheme}
        title="Toggle Light/Dark Mode"
      >
        {theme === "light" ? "🌙" : "☀️"}
      </button>

      {/* Header Bar */}
      <header className="app-header">
        <div className="brand" onClick={() => setActiveTab("Dashboard")}>
          <span className="brand-icon">🍦</span>
          <div className="brand-text">
            <h1>PRINE VACATION</h1>
            <p>Colorado 2026</p>
          </div>
        </div>

        {/* Dynamic Countdown */}
        <CountdownHeader />
      </header>

      {/* Main Container Layout */}
      <div className="app-container">
        {/* Sidebar Router Navigation */}
        <aside className="sidebar">
          <button className={`nav-item ${activeTab === "Dashboard" ? "active" : ""}`} onClick={() => setActiveTab("Dashboard")}>
            <span className="nav-item-icon">🏡</span> Dashboard
          </button>
          <button className={`nav-item ${activeTab === "Schedule" ? "active" : ""}`} onClick={() => setActiveTab("Schedule")}>
            <span className="nav-item-icon">🗓️</span> Schedule
          </button>
          <button className={`nav-item ${activeTab === "Flights" ? "active" : ""}`} onClick={() => setActiveTab("Flights")}>
            <span className="nav-item-icon">🛫</span> Flights
          </button>
          <button className={`nav-item ${activeTab === "Rooms" ? "active" : ""}`} onClick={() => setActiveTab("Rooms")}>
            <span className="nav-item-icon">🛌</span> Rooms
          </button>
          <button className={`nav-item ${activeTab === "Cars" ? "active" : ""}`} onClick={() => setActiveTab("Cars")}>
            <span className="nav-item-icon">🚗</span> Cars
          </button>
          <button className={`nav-item ${activeTab === "Grocery" ? "active" : ""}`} onClick={() => setActiveTab("Grocery")}>
            <span className="nav-item-icon">🛒</span> Grocery List
          </button>
          <button className={`nav-item ${activeTab === "Dinners" ? "active" : ""}`} onClick={() => setActiveTab("Dinners")}>
            <span className="nav-item-icon">🍳</span> Dinner
          </button>
          <button className={`nav-item ${activeTab === "Activities" ? "active" : ""}`} onClick={() => setActiveTab("Activities")}>
            <span className="nav-item-icon">🏔️</span> Activities
          </button>
          <button className={`nav-item ${activeTab === "BookClub" ? "active" : ""}`} onClick={() => setActiveTab("BookClub")}>
            <span className="nav-item-icon">📖</span> Book Club
          </button>
          <button className={`nav-item ${activeTab === "Packing" ? "active" : ""}`} onClick={() => setActiveTab("Packing")}>
            <span className="nav-item-icon">🎒</span> Packing List
          </button>
        </aside>

        {/* Content Render Area */}
        <main className="content-area">
          {activeTab === "Dashboard" && <DashboardView setActiveTab={setActiveTab} handleLogout={handleLogout} users={users} />}
          {activeTab === "Schedule" && <Schedule users={users} />}
          {activeTab === "Flights" && <FlightTrackerV3 users={users} />}
          {activeTab === "Rooms" && <RoomAssignments users={users} />}
          {activeTab === "Cars" && <CarAssignments users={users} />}
          {activeTab === "Grocery" && <GroceryList users={users} />}
          {activeTab === "Dinners" && <DinnerDishes users={users} />}
          {activeTab === "Activities" && <LocalActivities users={users} />}
          {activeTab === "BookClub" && <BookClub users={users} />}
          {activeTab === "Packing" && <PackingList users={users} />}
        </main>
      </div>
    </div>
  );
}

// Countdown Calculation Sub-Component
function CountdownHeader() {
  const [daysLeft, setDaysLeft] = useState(0);

  useEffect(() => {
    const calculateCountdown = () => {
      const vacationDate = new Date(`${TRIP_START_DATE}T00:00:00`);
      const diffTime = vacationDate - new Date();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDaysLeft(diffDays);
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 3600000); // refresh hourly
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="header-countdown">
      <span className="countdown-label">Countdown:</span>
      <span className="countdown-value">
        {daysLeft > 0 ? `${daysLeft} Days to Go!` : daysLeft === 0 ? "🎉 Arrival Day" : "🏖️ Vacation Mode"}
      </span>
    </div>
  );
}

// 44 Snarky Anxiety Remedies for the Panic Modal
const PANIC_REMEDIES = [
  "Put down your phone, grab a towel, apply SPF 50, and remember that no one has actually gotten lost in Edwards since the great signal tower upgrade of '19.",
  "Panic is a secondary emotion. The primary emotion is hunger. Go find some peanut butter pickle toast, or make Henry grill you a burger.",
  "If things get truly dire, just pretend your phone died and submerge yourself in the hot tub until the screaming stops.",
  "If you are reading this, you are still alive. Unfortunately, you are also still legally required to wash the dishes on your assigned night.",
  "Studies show that taking a deep breath of mountain air is 40% more effective when accompanied by a glass of chilled Pinot Grigio.",
  "Calm down. The GPS works, the mountains aren't moving, and Barb's itineraries are merely strong suggestions, not federal laws.",
  "If the family drama is reaching a boil, just look toward the horizon and pretend you are in a quiet, high-end medication commercial.",
  "Are you panicking, or are you just realizing that you forgot to pack underwear? Either way, the local Walmart is open.",
  "Taking a deep breath won't solve the grocery store coordinate mismatch, but it will prevent you from crying in the cereal aisle.",
  "No one is going to starve. Even if the dinner board is blank, there are approximately thirty restaurants in Edwards.",
  "Before you press this button again, ask yourself: 'Have I tried ignoring everyone and reading a book club suggestion instead?'",
  "The mountains are calling, and they are saying: 'Please quiet down, we can hear your family discussion from Vail Pass.'",
  "You are in a luxury Colorado pool villa. If this is a crisis, the rest of the world would like to trade places with your crisis.",
  "Pro-tip: If you close your eyes and listen to the wind, you can almost hear the sound of the grocery list updating itself in real-time.",
  "Everything is under control. And if it isn't, there is a very nice Facebook Messenger link on the home page where you can go yell.",
  "Breathe. The villa has Wi-Fi, the fridge has shelves, and your room assignment has a door that locks.",
  "If the chaos has reached critical mass, remember: you are surrounded by people who have seen you at your worst and still showed up to Colorado.",
  "This is not an emergency. An emergency is no coffee. An emergency is no hot water. An emergency is not 'I don't know what we're doing at 2pm.'",
  "The cure for whatever this is: step outside, look up, confirm that the Rockies are still there, and adjust your attitude accordingly.",
  "Whatever the argument is about, it is not worth it. Unless it is about whether to do the lake or the hot tub first, in which case: both. Obviously both.",
  "You have survived every previous family vacation. Statistically, you are going to survive this one too. Probably.",
  "Before escalating further, confirm: has everyone eaten in the last four hours? If not, this is a snack problem wearing a crisis costume.",
  "There are exactly zero problems in this villa that cannot be temporarily improved by a walk, a nap, or a very large glass of something cold.",
  "Alert: you are in one of the most beautiful places on earth, surrounded by people who chose to be here with you. Recalibrate accordingly.",
  "This too shall pass. Probably by dinner. Definitely by the second round of drinks.",
  "Whatever is happening, Sonoka has almost certainly seen worse and is quietly handling it. Let Sonoka handle it.",
  "You do not need a plan. You need a snack, twenty minutes of silence, and to accept that this family has never once agreed on a movie and never will.",
  "The hot tub fits everyone, including the drama. Submerge it. Leave it there.",
  "No one else remembered to buy coffee filters either. We adapted. We survived. We can handle whatever this is.",
  "Good news: whatever went wrong, it is going to be an absolutely incredible story at next year's vacation. Bad news: you have to get through tonight first.",
  "If the situation feels unmanageable, find Katie or Autumn. Between the two of them, they have already solved three problems you don't even know about yet.",
  "Locate the nearest child. If it is Elizabeth, she will explain the situation calmly and in complete sentences. If it is Brooks, he has already moved on and you should too.",
  "Harper has already assessed the vibe, clocked everyone's mood, and has a plan. Find Harper. Trust Harper.",
  "Benny and Jacob have handled worse before lunch. Whatever this is, bring it to them and then step back and watch it get handled.",
  "Martín and Aisa are probably already doing something impressive nearby. Follow their lead and at minimum you will end up somewhere more fun than where you are now.",
  "Linda and Barb have collectively weathered decades of family chaos with grace, humor, and an uncanny ability to produce snacks from nowhere. Seek them out immediately.",
  "Valerie and Reilan are having a better time than you right now. Find out where they are and go be near that energy.",
  "Shay has a gift for making hard things look easy and stressful moments feel manageable. If the panic is real, Shay is the move.",
  "If all else fails, find the smallest kid available — Aisa, Reilan, Martín, whoever is closest — and watch them for sixty seconds. They have already forgotten the problem. You can too."
];

// Beautiful Summary Dashboard Overview
function DashboardView({ setActiveTab, handleLogout, users = [] }) {
  const userName = localStorage.getItem("prine_user_name") || "Family Member";

  const getUserName = (id) => {
    if (!id) return "";
    const foundById = users.find(u => String(u.id) === String(id));
    if (foundById) return foundById.name;
    const foundByName = users.find(u => u.name.toLowerCase().trim() === String(id).toLowerCase().trim());
    if (foundByName) return foundByName.name;
    return id;
  };

  const [allStatuses, setAllStatuses] = useState([]);
  const [myStatusText, setMyStatusText] = useState("");
  const [myStatusEmoji, setMyStatusEmoji] = useState("📍");
  const [showPanicModal, setShowPanicModal] = useState(false);
  const [commentInputs, setCommentInputs] = useState({});
  const [expandedDiscussionIds, setExpandedDiscussionIds] = useState({});
  const [allPanics, setAllPanics] = useState([]);
  const [selectedRemedy, setSelectedRemedy] = useState("");
  const [weather, setWeather] = useState({ current: null, high: null, low: null, loading: true });

  const getRelativeTimeString = (timestamp) => {
    if (!timestamp) return "Active Now";
    const diffMs = Date.now() - timestamp;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins === 1) return "1 minute ago";
    if (diffMins < 60) return `${diffMins} minutes ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return "1 hour ago";
    if (diffHours < 24) return `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "1 day ago";
    return `${diffDays} days ago`;
  };

  useEffect(() => {
    const unsub = subscribeToCollection("panics", (data) => {
      setAllPanics(data || []);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = subscribeToCollection("statuses", (data) => {
      setAllStatuses(data);
    }, []);
    return unsub;
  }, []);

  // Fetch real weather forecast from Open-Meteo for Edwards, CO coordinate (lat: 39.6453, lon: -106.5915)
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await fetch(
          "https://api.open-meteo.com/v1/forecast?latitude=39.6453&longitude=-106.5915&current=temperature_2m&daily=temperature_2m_max,temperature_2m_min&temperature_unit=fahrenheit&timezone=America%2FDenver"
        );
        if (response.ok) {
          const data = await response.json();
          setWeather({
            current: Math.round(data.current.temperature_2m),
            high: Math.round(data.daily.temperature_2m_max[0]),
            low: Math.round(data.daily.temperature_2m_min[0]),
            loading: false
          });
        } else {
          setWeather(prev => ({ ...prev, loading: false }));
        }
      } catch (err) {
        console.error("Error fetching live weather:", err);
        setWeather(prev => ({ ...prev, loading: false }));
      }
    };
    fetchWeather();
  }, []);

  const currentMyStatus = allStatuses
    .filter(s => s.name === userName || s.id === userName)
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0];
  const currentMyStatusEmoji = currentMyStatus ? currentMyStatus.emoji : null;

  useEffect(() => {
    if (currentMyStatusEmoji) {
      setMyStatusEmoji(currentMyStatusEmoji);
    }
  }, [currentMyStatusEmoji]);

  // Lock scrolling on document body when Panic Modal is open
  useEffect(() => {
    if (showPanicModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showPanicModal]);

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    if (!myStatusText.trim()) return;
    const statusId = `status_${userName}_${Date.now()}`;
    await saveDocument("statuses", statusId, {
      name: userName,
      status: myStatusText.trim(),
      emoji: myStatusEmoji || "📍",
      timestamp: Date.now()
    });
    setMyStatusText("");
  };

  const handleDeleteStatus = async (statusId) => {
    if (!window.confirm("Are you sure you want to delete this status?")) return;
    await deleteDocument("statuses", statusId);
  };

  const handleStatusLike = async (person) => {
    if (!userName || userName === "Family Member") return;
    const likedBy = Array.isArray(person.likedBy) ? person.likedBy : [];
    const alreadyLiked = likedBy.includes(userName);
    const newLikedBy = alreadyLiked
      ? likedBy.filter(u => u !== userName)
      : [...likedBy, userName];
    await saveDocument("statuses", person.id, {
      ...person,
      likedBy: newLikedBy,
      likes: newLikedBy.length
    });
  };

  const handleAddStatusComment = async (person) => {
    const text = (commentInputs[person.id] || "").trim();
    if (!text || !userName || userName === "Family Member") return;
    const existing = Array.isArray(person.comments) ? person.comments : [];
    const newComment = {
      id: `c_${Date.now()}`,
      user: userName,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      text
    };
    await saveDocument("statuses", person.id, {
      ...person,
      comments: [...existing, newComment]
    });
    setCommentInputs(prev => ({ ...prev, [person.id]: "" }));
  };

  const handleDeleteStatusComment = async (person, commentId) => {
    const existing = Array.isArray(person.comments) ? person.comments : [];
    await saveDocument("statuses", person.id, {
      ...person,
      comments: existing.filter(c => c.id !== commentId)
    });
  };

  const handleOpenPanic = async () => {
    const randomIndex = Math.floor(Math.random() * PANIC_REMEDIES.length);
    setSelectedRemedy(PANIC_REMEDIES[randomIndex]);
    setShowPanicModal(true);

    // Get formatted local date string YYYY-MM-DD
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;

    // Look up count for today for this user
    const myTodayDoc = allPanics.find(p => p.date === todayStr && p.user === userName);
    const currentCount = myTodayDoc ? (myTodayDoc.count || 0) : 0;

    // Save document keyed by date and username to allow aggregate summation and user totals
    const docId = `${todayStr}_${userName}`;
    await saveDocument("panics", docId, {
      date: todayStr,
      user: userName,
      count: currentCount + 1
    });
  };

  const totalPanicsToday = (() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;

    return allPanics
      .filter(p => p.date === todayStr)
      .reduce((sum, item) => sum + (item.count || 0), 0);
  })();

  return (
    <div className="tab-panel">
      {/* Light Mode overrides for Panic Modal */}
      <style dangerouslySetInnerHTML={{
        __html: `
        :root[data-theme="light"] .panic-overlay {
          background: rgba(239, 68, 68, 0.08) !important;
        }
        :root[data-theme="light"] .panic-content {
          background: #ffffff !important;
          border-color: rgba(239, 68, 68, 0.25) !important;
          box-shadow: 0 20px 50px rgba(239, 68, 68, 0.15) !important;
          color: #0f172a !important;
        }
        :root[data-theme="light"] .panic-content h3 {
          color: #dc2626 !important;
        }
        :root[data-theme="light"] .panic-content .modal-close {
          color: #475569 !important;
        }
        :root[data-theme="light"] .panic-content .modal-close:hover {
          color: #0f172a !important;
        }
        :root[data-theme="light"] .panic-title {
          color: #dc2626 !important;
        }
        :root[data-theme="light"] .panic-remedy-box {
          background: rgba(239, 68, 68, 0.03) !important;
          border-color: rgba(239, 68, 68, 0.2) !important;
          color: #991b1b !important;
        }
      `}} />

      {/* Warm Welcome Block */}
      <div className="view-title-container" style={{ marginBottom: '24px' }}>
        <div className="view-title">
          <h2>👋 Welcome back, {getUserName(userName)}!</h2>
        </div>
      </div>

      {/* Primary Row: House, Weather, and Links Column */}
      <div className="house-weather-grid">

        {/* House Details Card (Takes up ~38% of the row) */}
        <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '16px', padding: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Fixed-width wrapper guarantees perfectly aligned vertical columns */}
            <div style={{ width: '64px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: '3.5rem', lineHeight: 1 }}>🏡</span>
            </div>
            <div>
              <span style={{ fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', color: 'var(--color-primary)' }}>House Details</span>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', lineHeight: '1.6', wordBreak: 'normal', overflowWrap: 'break-word', margin: '4px 0 0 0' }}>
                📍 <strong>Address:</strong>{' '}
                <a
                  href="https://maps.app.goo.gl/Ek5PFLm2mE8FKwEK8"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'var(--color-primary)',
                    textDecoration: 'none',
                    borderBottom: '1px dashed rgba(2, 132, 199, 0.4)',
                    transition: 'color 0.2s ease, border-bottom-color 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = 'var(--color-secondary)';
                    e.currentTarget.style.borderBottomColor = 'var(--color-secondary)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = 'var(--color-primary)';
                    e.currentTarget.style.borderBottomColor = 'rgba(2, 132, 199, 0.4)';
                  }}
                >
                  81 Aspen Meadow Dr, Edwards, CO, 81632
                </a>{' '}<br />
                📶 <strong>Wifi:</strong> TBD <br />
                🔑 <strong>Password:</strong> TBD
              </p>
            </div>
          </div>

          <div style={{ marginTop: 'auto', borderTop: '1px solid var(--color-glass-border)', paddingTop: '16px' }}>
            <a
              href="https://vrbo.onelink.me/ItNz/en2qur66"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '0.8rem', color: 'var(--color-primary)', fontWeight: '600', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              View House Listing on VRBO ↗️
            </a>
          </div>
        </div>

        {/* 2x2 Grid of Smaller Cards */}
        <div className="dashboard-links-grid">

          {/* Live Weather Forecast Card */}
          <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '8px', padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '1.8rem', lineHeight: 1, filter: 'drop-shadow(0 4px 12px rgba(245, 158, 11, 0.2))' }}>☀️</span>
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <span style={{ fontSize: '0.6rem', fontWeight: '700', textTransform: 'uppercase', color: 'var(--color-secondary)', display: 'block', lineHeight: '1' }}>Edwards Weather</span>
                {weather.loading ? (
                  <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '0.95rem', color: 'var(--color-text-muted)', margin: '2px 0 0 0', fontWeight: '600' }}>Loading...</h3>
                ) : (
                  <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1rem', color: 'var(--color-text-main)', margin: '2px 0 0 0', fontWeight: '700', display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                    {weather.current !== null ? `${weather.current}°F` : "76°F"}
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: '500' }}>
                      ({weather.high !== null ? `${weather.high}°` : "76°"}/{weather.low !== null ? `${weather.low}°` : "48°"})
                    </span>
                  </h3>
                )}
              </div>
            </div>
            <div style={{ marginTop: 'auto', borderTop: '1px solid var(--color-glass-border)', paddingTop: '8px' }}>
              <a
                href="https://weather.com/weather/today/l/19d8b122b9af6b0be65c08f1b9145c508f7cc26268abdb322bfdf8ed728b7322"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: '600', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                See Forecast ↗️
              </a>
            </div>
          </div>

          {/* Messenger Card */}
          <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '8px', padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '1.8rem', lineHeight: 1, filter: 'drop-shadow(0 4px 12px rgba(2, 132, 199, 0.2))' }}>💬</span>
              </div>
              <div>
                <span style={{ fontSize: '0.6rem', fontWeight: '700', textTransform: 'uppercase', color: 'var(--color-primary)', display: 'block', lineHeight: '1' }}>Group Chat</span>
                <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1rem', color: 'var(--color-text-main)', margin: '2px 0 0 0', fontWeight: '700' }}>
                  Messenger Group
                </h3>
              </div>
            </div>
            <div style={{ marginTop: 'auto', borderTop: '1px solid var(--color-glass-border)', paddingTop: '8px' }}>
              <a
                href="https://www.messenger.com/t/1606319960456011/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: '600', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                Launch Chat ↗️
              </a>
            </div>
          </div>

          {/* Spotify Playlist Card */}
          <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '8px', padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '1.8rem', lineHeight: 1, filter: 'drop-shadow(0 4px 12px rgba(16, 185, 129, 0.2))' }}>🎵</span>
              </div>
              <div>
                <span style={{ fontSize: '0.6rem', fontWeight: '700', textTransform: 'uppercase', color: 'var(--color-accent)', display: 'block', lineHeight: '1' }}>Summer Tunes</span>
                <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1rem', color: 'var(--color-text-main)', margin: '2px 0 0 0', fontWeight: '700' }}>
                  Spotify Playlist
                </h3>
              </div>
            </div>
            <div style={{ marginTop: 'auto', borderTop: '1px solid var(--color-glass-border)', paddingTop: '8px' }}>
              <a
                href="https://open.spotify.com/playlist/1v3mimoEXBf167lEmY479u?si=496eb84797584141&pt=b9ea9e64037948e42ffd361b7a9e3199"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: '600', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                Listen Now ↗️
              </a>
            </div>
          </div>

          {/* Google Photos Album Card */}
          <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '8px', padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '1.8rem', lineHeight: 1, filter: 'drop-shadow(0 4px 12px rgba(245, 158, 11, 0.2))' }}>📸</span>
              </div>
              <div>
                <span style={{ fontSize: '0.6rem', fontWeight: '700', textTransform: 'uppercase', color: 'var(--color-secondary)', display: 'block', lineHeight: '1' }}>Shared Album</span>
                <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1rem', color: 'var(--color-text-main)', margin: '2px 0 0 0', fontWeight: '700' }}>
                  Google Photos
                </h3>
              </div>
            </div>
            <div style={{ marginTop: 'auto', borderTop: '1px solid var(--color-glass-border)', paddingTop: '8px' }}>
              <a
                href="https://photos.app.goo.gl/6A2ZdbYpnwXW2brs8"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: '600', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                Open Album ↗️
              </a>
            </div>
          </div>

        </div>
      </div>

      {/* Secondary Row: Unified Family Status Board */}
      <div className="dashboard-secondary-grid" style={{ marginBottom: '32px' }}>
        <div className="glass-panel" style={{ padding: '28px' }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.4rem', color: 'var(--color-text-main)', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            📍 Family Status Board
          </h3>
          <div className="status-board-content">
            {/* Left Column: Update Status Form */}
            <div className="status-board-left">
              <h4 style={{ fontFamily: 'var(--font-title)', fontSize: '1.1rem', color: 'var(--color-text-main)', marginBottom: '12px' }}>Your Status</h4>
              {currentMyStatus ? (
                <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(2, 132, 199, 0.05)', borderRadius: '10px', borderLeft: '3px solid var(--color-primary)' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', color: 'var(--color-primary)', display: 'block', marginBottom: '4px' }}>Current Status</span>
                  <p style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--color-text-main)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '1.25rem' }}>{currentMyStatus.emoji || "📍"}</span>
                    {currentMyStatus.status}
                  </p>
                  <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: '4px', display: 'block' }}>Updated {getRelativeTimeString(currentMyStatus.timestamp)}</span>
                </div>
              ) : (
                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', lineHeight: '1.5', marginBottom: '16px' }}>
                  Let the family know where you are or what you're up to (e.g. napping, pool deck, hiking).
                </p>
              )}

              <form onSubmit={handleUpdateStatus} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    maxLength={1}
                    value={myStatusEmoji}
                    onChange={e => setMyStatusEmoji(e.target.value.slice(0, 1))}
                    style={{
                      width: '54px',
                      textAlign: 'center',
                      fontSize: '1.2rem',
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid var(--color-glass-border)',
                      color: 'var(--color-text-main)',
                      fontFamily: 'var(--font-body)',
                      outline: 'none'
                    }}
                    placeholder="📍"
                    title="Custom status emoji"
                  />
                  <input
                    type="text"
                    placeholder="e.g. Sorting puzzle pieces"
                    className="input-field"
                    style={{ flex: 1, fontSize: '0.85rem', padding: '10px 12px', minWidth: 0 }}
                    value={myStatusText}
                    onChange={e => setMyStatusText(e.target.value)}
                    required
                  />
                </div>

                {/* Predefined Quick Emoji Chips */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '2px' }}>
                  {["📍", "🏔️", "🏊‍♂️", "🍔", "💭", "💛", "🕶️", "🎲", "🍿", "🪩", "📖", "😴"].map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setMyStatusEmoji(emoji)}
                      style={{
                        background: myStatusEmoji === emoji ? 'var(--color-primary-semi, rgba(2, 132, 199, 0.2))' : 'rgba(255, 255, 255, 0.05)',
                        border: `1px solid ${myStatusEmoji === emoji ? 'var(--color-primary)' : 'var(--color-glass-border)'}`,
                        borderRadius: '20px',
                        padding: '4px 10px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        transition: 'all 0.2s ease',
                        fontFamily: 'var(--font-body)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  <button type="submit" className="btn-primary" style={{ flex: 1, fontSize: '0.8rem', padding: '8px 12px', justifyContent: 'center' }}>
                    Update Status
                  </button>
                </div>
              </form>
            </div>

            {/* Right Column: Family Status Feed */}
            <div className="status-board-right" style={{ display: 'flex', flexDirection: 'column' }}>
              <h4 style={{ fontFamily: 'var(--font-title)', fontSize: '1.1rem', color: 'var(--color-text-main)', marginBottom: '16px' }}>Status Feed</h4>
              {allStatuses.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.9rem', fontStyle: 'italic', margin: 'auto' }}>
                  No one has checked in yet. Be the first to update your status!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '520px', overflowY: 'auto', paddingRight: '4px' }}>
                  {[...allStatuses].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)).map(person => {
                    const likedBy = Array.isArray(person.likedBy) ? person.likedBy : [];
                    const hasLiked = likedBy.includes(userName);
                    const comments = Array.isArray(person.comments) ? person.comments : [];
                    const isStatusExpanded = expandedDiscussionIds[person.id] !== undefined
                      ? expandedDiscussionIds[person.id]
                      : comments.length > 0;
                    return (
                      <div
                        key={person.id}
                        className="glass-panel"
                        style={{
                          background: 'var(--color-room-box-bg)',
                          border: '1px solid var(--color-room-box-border)',
                          borderRadius: '12px',
                          padding: '16px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px',
                          boxShadow: 'var(--shadow-premium)',
                          transition: 'all 0.2s ease',
                          margin: 0
                        }}
                      >
                        {/* Header: name + time + delete */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                          <span style={{ fontWeight: '700', color: 'var(--color-primary)', fontSize: '0.85rem' }}>
                            {person.emoji || "📍"} {getUserName(person.name || person.id)}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                              {getRelativeTimeString(person.timestamp)}
                            </span>
                            {(person.name === userName || person.id === userName) && (
                              <button
                                onClick={() => handleDeleteStatus(person.id)}
                                className="btn-inline-delete-cross"
                                style={{ lineHeight: 1 }}
                                title="Delete Status"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Status text */}
                        <p style={{ fontSize: '0.95rem', fontWeight: '600', color: 'var(--color-text-main)', margin: 0, lineHeight: '1.4' }}>
                          <span style={{ wordBreak: 'break-word' }}>{person.status}</span>
                        </p>

                        {/* Actions Row (Like + Reply) */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <VoteButton
                            votes={likedBy.length}
                            hasVoted={hasLiked}
                            onVote={() => handleStatusLike(person)}
                            votedBy={likedBy}
                            users={users}
                            itemType="status"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedDiscussionIds(prev => ({
                                ...prev,
                                [person.id]: !isStatusExpanded
                              }));
                            }}
                            style={{
                              background: isStatusExpanded ? 'rgba(2, 132, 199, 0.12)' : 'rgba(255, 255, 255, 0.03)',
                              border: isStatusExpanded ? '1px solid rgba(2, 132, 199, 0.3)' : '1px solid rgba(255, 255, 255, 0.08)',
                              borderRadius: '20px',
                              padding: '4px 10px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              color: isStatusExpanded ? 'var(--color-primary)' : 'var(--color-text-muted)',
                              fontWeight: '600',
                              transition: 'all 0.2s ease',
                              fontFamily: 'var(--font-body)'
                            }}
                            title="Reply / Discussion"
                          >
                            💬 {comments.length}
                          </button>
                        </div>

                        {/* Discussion thread */}
                        <DiscussionThread
                          comments={comments}
                          commentInput={commentInputs[person.id] || ""}
                          onCommentInputChange={(val) => setCommentInputs(prev => ({ ...prev, [person.id]: val }))}
                          onAddComment={() => handleAddStatusComment(person)}
                          onDeleteComment={(commentId) => handleDeleteStatusComment(person, commentId)}
                          placeholder="Reply to this status..."
                          users={users}
                          isExpanded={isStatusExpanded}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Massive Panic Button Trigger */}
      <div className="panic-button-wrapper" style={{ marginTop: '64px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
        <button className="btn-panic-massive" onClick={handleOpenPanic}>
          🚨 PANIC!!! 🚨
        </button>
      </div>

      {/* High-fidelity Panic Overlay Modal */}
      {showPanicModal && (
        <div className="modal-overlay panic-overlay">
          <div className="modal-content panic-content" style={{ maxWidth: '580px', borderRadius: '24px' }}>
            <div className="modal-header" style={{ borderColor: 'rgba(239, 68, 68, 0.2)', padding: '24px 28px' }}>
              <h3 style={{ color: 'var(--color-danger)', fontSize: '1.4rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🚨 Minor Panic Management
              </h3>
              <button className="modal-close" onClick={() => setShowPanicModal(false)}>×</button>
            </div>

            <div className="modal-body panic-body" style={{ padding: '32px', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
              <div className="panic-title" style={{ fontSize: '3rem', margin: 0 }}>CHILLAX! 🍹</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--color-danger)', fontWeight: '700' }}>
                {totalPanicsToday > 0
                  ? `Family members have panicked ${totalPanicsToday} ${totalPanicsToday === 1 ? 'time' : 'times'} today. Stay strong!`
                  : "No panic events logged today yet."}
              </div>
              <div
                className="panic-remedy-box"
                style={{
                  width: '100%',
                  background: 'rgba(239, 68, 68, 0.04)',
                  border: '2px solid rgba(239, 68, 68, 0.25)',
                  boxShadow: '0 10px 30px rgba(239, 68, 68, 0.08)',
                  padding: '24px',
                  borderRadius: '16px',
                  fontSize: '1.15rem',
                  color: 'var(--color-text-main)',
                  lineHeight: '1.6',
                  textAlign: 'left',
                  fontWeight: '600',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{ position: 'absolute', top: '-10px', right: '-10px', fontSize: '4rem', opacity: 0.07, pointerEvents: 'none' }}>🧘‍♀️</div>
                {selectedRemedy || "Put down your phone, grab a towel, apply SPF 50, jump into the luxury villa pool, and take a long breath. Colorado summer is wonderful."}
              </div>

              <button
                className="btn-primary"
                style={{
                  background: 'linear-gradient(135deg, var(--color-danger), #dc2626)',
                  borderColor: 'transparent',
                  marginTop: '16px',
                  padding: '12px 32px',
                  fontSize: '0.95rem',
                  borderRadius: '24px',
                  boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontFamily: 'var(--font-body)'
                }}
                onClick={() => setShowPanicModal(false)}
              >
                I Am Calmer Now, Stop Panicking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Out Button at the very bottom */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px', paddingBottom: '20px' }}>
        <button
          className="btn-secondary"
          style={{
            fontSize: '0.8rem',
            padding: '8px 16px',
            borderRadius: '20px',
            borderColor: 'rgba(239, 68, 68, 0.3)',
            color: 'var(--color-danger)',
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontFamily: 'var(--font-body)'
          }}
          onClick={handleLogout}
        >
          Logout
        </button>
      </div>
    </div>
  );
}

export default App;
